import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';
import styled from '@emotion/styled';
import { useTheme } from 'emotion-theming';
import { useDispatch, useSelector } from 'react-redux';
import { CardElement, injectStripe } from 'react-stripe-elements';
import { Field, Form } from 'formik';
import moment from 'moment';
import Logger from 'js-logger';
import Shimmer from 'react-shimmer-effect';

import InfoCard from './InfoCard';
import PaymentCard from './PaymentCard';
import NoHoverButton from '../../../reseller/components/presentational/programs/NoHoverButton';
import Spinner from '../../../../shared/_TBComponents/Spinner';
import Text from '../../../../shared/_TBComponents/Text';
import {
  clearCoupon,
  clearCouponError,
  fetchCouponDetails,
  fetchStripeDetails,
  setCouponError,
  setPaymentLoading,
} from '../../ducks/programsActions';

const CHECKOUT_TIMEOUT_MS = 750;
const CENTS_TO_DOLLARS = 100;
const FULL_PERCENTAGE = 100;
const YEARLY_BILLING_FREQUENCY = 12;
const ONE_TIME_BILLING_FREQUENCY = 0;

const ButtonPadding = styled('div')`
  @media screen and (max-width: 768px) {
    padding-bottom: 50px;
  }
`;

const CouponButtonContainer = styled('div')`
  width: 20%;
`;

const CouponCodeContainer = styled('div')`
  display: flex;
  flex-direction: column;
  margin-top: 20px;
`;

const CouponInputAndButtonWrapper = styled('div')`
  align-items: center;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;

const CouponInputContainer = styled('div')`
  height: 38px;
  width: 75%;

  #coupon-field {
    height: 100%;
  }
`;

const DefaultPaymentWrapper = styled('div')`
  background-color: transparent;

  .card-info-box {
    display: flex;
    flex-direction: column;
    font-size: 12px;
  }

  .StripeElement {
    border: 1px solid ${(props) => props.theme.colors.border || '#dbdbdb'};
    border-radius: 5px;
    padding-top: 8px;
  }
`;

const FieldError = styled('div')`
  color: ${(props) => props.theme.colors.error || '#ff6600'};
  font-size: 14px;
  margin: 10px 0px;
`;

const FormGroup = styled('div')`
  z-index: 1;

  input[type="checkbox"] {
    -moz-border-radius: 0px !important;
    -webkit-border-radius: 0px !important;
    border-radius: 0px !important;
  }

  label {
    width: 100%;
  }

  button {
    color: white;
  }

  .easy-join {
    margin-bottom: 15px;
    margin-top: 30px;
  }

  .spinner-container {
    min-height: 60px;
  }

  &.prices {
    display: flex;
    justify-content: space-between;
    margin-top: -5px;
  }

  .coupon-container {
    width: 75%;
  }

  .apply-container {
    margin-top: 25px;
    width: 20%;
  }

  .apply-button {
    height: 38px;
  }

  &.card-entry {
    margin: 15px 0px 0px 0px;
  }

  .checkbox-input {
    margin-right: 10px;
    margin-top: 2px;
    width: 55px;
  }

  .StripeElement {
    font-size: 18px;
  }

  &.d-flex {
    margin-bottom: 10px;
  }

  &.less-margin {
    margin-bottom: -10px;
  }

  &.prices-wrapper {
    padding-top: 20px;
  }

  .update-button {
    align-self: flex-end;
    margin-bottom: 20px;
    margin-left: auto;
    margin-top: 10px;
  }

  .Shimmer-shimmer-0-1-1 {
    color: transparent;
  }

  @media screen and (max-width: 600px) {
    a {
      font-size: 11px;
    }
  }
`;

const InfoDetailsWrapper = styled('div')``;

const LargePrice = styled('span')`
  color: ${(props) => props.theme.textColor};
  font-size: 40px;
  font-weight: 900;
  text-transform: uppercase;
`;

const PriceContainer = styled('span')`
  display: flex;
  flex-direction: column;
  text-align: center;
`;

const SmallPrice = styled('span')`
  color: ${(props) => props.theme.textColor};
  font-size: 12px;
  text-transform: uppercase;
`;

const FormCheckout = ({
  elements,
  formProps,
  stripe,
}) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { resellerSlug } = useParams();

  const [isUsingSavedCard, setIsUsingSavedCard] = useState(false);
  const [timeoutRef, setTimeoutRef] = useState(null);

  const coupon = useSelector((state) => state.programs.data.coupon);
  const couponError = useSelector((state) => state.programs.data.couponError);
  const currentUser = useSelector((state) => state.auth.data.currentUser);
  const isLoadingCoupon = useSelector(
    (state) => state.programs.ui.isLoadingCoupon,
  );
  const isLoadingPayment = useSelector(
    (state) => state.programs.ui.isLoadingPayment,
  );
  const isLoadingPaymentDetails = useSelector(
    (state) => state.programs.ui.isLoadingPaymentDetails,
  );
  const paymentDetails = useSelector(
    (state) => state.programs.data.paymentDetails,
  );
  const paymentError = useSelector((state) => state.programs.data.paymentError);
  const programData = useSelector((state) => state.programs.data.programData);

  const {
    errors,
    handleSubmit,
    setFieldValue,
    touched,
    values,
  } = formProps;

  const {
    accountCode,
    organizationName,
    resellerSubscriptions,
    stripeId,
  } = currentUser;

  const {
    billingFrequency,
    billingFrequencyDescription,
    currencySymbol,
    numBillingCycles,
    placeSymbolAfterCurrency,
    programName,
    programPrice,
    resellerAccountCode,
    resellerName,
    trialPeriod,
  } = programData;

  const price = programPrice / CENTS_TO_DOLLARS;

  useEffect(() => {
    if (stripeId && Object.keys(paymentDetails).length === 0) {
      Logger.debug('Payment Details:', paymentDetails);
      dispatch(fetchStripeDetails(accountCode));
    }
  }, [accountCode, dispatch, paymentDetails, stripeId]);

  useEffect(() => {
    const hasPaymentSources = paymentDetails.sources?.data?.length > 0;
    if (hasPaymentSources) {
      setIsUsingSavedCard(true);
      setFieldValue('stripeCardId', paymentDetails?.default_source);
      Logger.debug('Set Saved Card Condition');
    } else {
      setIsUsingSavedCard(false);
    }
  }, [paymentDetails, setFieldValue]);

  const calculateTotal = () => {
    let calculatedTotal = price;

    if (coupon) {
      if (coupon.amountOff) {
        if (coupon.currencySymbol !== currencySymbol) {
          return price;
        }
        calculatedTotal = price - (coupon.amountOff / CENTS_TO_DOLLARS);
      } else if (coupon.percentOff && coupon.percentOff > 0) {
        if (coupon.percentOff >= FULL_PERCENTAGE) {
          calculatedTotal = 0;
        } else {
          calculatedTotal = price * (1 - coupon.percentOff / FULL_PERCENTAGE);
        }
      }
    }

    return calculatedTotal < 0 ? 0 : parseFloat(calculatedTotal).toFixed(2);
  };

  useEffect(() => {
    if (coupon?.amountOff && coupon.currencySymbol !== currencySymbol) {
      dispatch(setCouponError('Coupon currency must match Program currency'));
    }
  }, [coupon, currencySymbol, dispatch]);

  const total = calculateTotal();

  const formatCurrency = (amount) => {
    const formatted = parseFloat(amount).toFixed(2);
    if (placeSymbolAfterCurrency) {
      return `${formatted}${currencySymbol}`;
    }
    return `${currencySymbol}${formatted}`;
  };

  const formatCurrencyNegative = (amount) => {
    const formatted = parseFloat(amount).toFixed(2);
    if (placeSymbolAfterCurrency) {
      return `-${formatted}${currencySymbol}`;
    }
    return `-${currencySymbol}${formatted}`;
  };

  const getPriceAndCurrency = () => {
    if (coupon.amountOff) {
      const amount = coupon.amountOff / CENTS_TO_DOLLARS;
      return formatCurrencyNegative(amount);
    }
    if (coupon.placeSymbolAfterCurrency) {
      const discountAmount = price * (coupon.percentOff / FULL_PERCENTAGE);
      return formatCurrencyNegative(discountAmount);
    }
    const discountAmount = price * (coupon.percentOff / FULL_PERCENTAGE);
    return formatCurrencyNegative(discountAmount);
  };

  const getProgramEndDate = () => {
    if (!numBillingCycles || numBillingCycles <= 0) {
      return '* Subscription will be ongoing';
    }

    if (billingFrequency === YEARLY_BILLING_FREQUENCY) {
      return `* Subscription will end on ${moment()
        .add(numBillingCycles, 'years')
        .subtract(1, 'days')
        .format('MMM D, YYYY')}`;
    }

    const numMonthsUntilExpires = numBillingCycles * billingFrequency;
    return `* Subscription will end on ${moment()
      .add(numMonthsUntilExpires, 'months')
      .subtract(1, 'days')
      .format('MMM D, YYYY')}`;
  };

  const getDefaultCard = () => {
    if (!paymentDetails.sources?.data) {
      return null;
    }
    return paymentDetails.sources.data.find(
      (card) => card.id === paymentDetails.default_source,
    );
  };

  const handleStripeSubmit = (ev) => {
    ev.preventDefault();

    const cardElement = elements.getElement('card');

    stripe
      .createPaymentMethod({
        billing_details: { name: values.name },
        card: cardElement,
        type: 'card',
      })
      .then(({ paymentMethod }) => {
        setFieldValue('paymentMethodId', paymentMethod.id);
        Logger.debug('Received Stripe PaymentMethod:', paymentMethod);
      });

    stripe
      .createToken({ name: values.name, type: 'card' })
      .then((token) => {
        Logger.debug('Stripe Token:', token);
        setFieldValue('stripeToken', token.token.id);
        handleSubmit();
      })
      .catch((err) => {
        Logger.debug('Stripe Token Error:', err);
        dispatch(setPaymentLoading(false));
      });
  };

  const handleCheckoutSubmit = (e) => {
    if (timeoutRef) {
      clearTimeout(timeoutRef);
    }

    if (!coupon || (coupon && couponError)) {
      delete values.couponId;
    }

    Logger.debug('Are we using a saved Card?', isUsingSavedCard);

    if (isUsingSavedCard) {
      setFieldValue('name', ' ');
      setTimeoutRef(setTimeout(() => handleSubmit(), CHECKOUT_TIMEOUT_MS));
    } else {
      setTimeoutRef(setTimeout(() => handleStripeSubmit(e), CHECKOUT_TIMEOUT_MS));
      dispatch(setPaymentLoading(true));
    }

    Logger.debug('Should be submitting');
  };

  const handleApplyCoupon = () => {
    dispatch(fetchCouponDetails(resellerSlug, values.couponId));
  };

  const handleCouponChange = (e) => {
    setFieldValue('couponId', e.target.value);

    if (coupon) {
      dispatch(clearCoupon());
    }
    if (couponError) {
      dispatch(clearCouponError());
    }
  };

  const handleAgreementChange = () => {
    setFieldValue('agreementData', !values.agreementData);
    Logger.debug(values);
  };

  const handleUpdateButton = () => {
    const newValue = !isUsingSavedCard;
    setFieldValue(
      'stripeCardId',
      newValue ? paymentDetails?.default_source : '',
    );
    setIsUsingSavedCard(newValue);
  };

  const isAlreadySubscribed = resellerSubscriptions?.length > 0;
  const isAdmin = currentUser?.admin !== 0;
  const isNotWithThisAccount = accountCode !== resellerAccountCode;
  const isCheckoutDisabled = errors.password
    || (errors.name && !isUsingSavedCard)
    || errors.agreementData
    || isAlreadySubscribed
    || isLoadingCoupon;

  const priceTextProps = {
    fontWeight: 600,
    fontSize: theme.textFontSizes[1],
  };

  const defaultCardFields = () => (
    <DefaultPaymentWrapper>
      <FormGroup>
        {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
        <label htmlFor='name'>
          <Text styledTextProps={{ fontSize: theme.textFontSizes[0] }}>
            Name on Card
          </Text>
          <Field
            className='form-control'
            id='name'
            name='name'
            placeholder='First and Last Name'
            type='text'
          />
        </label>
        {errors.name && touched.name && (
          <FieldError className='text-center'>{errors.name}</FieldError>
        )}
      </FormGroup>
      <FormGroup className='card-entry form-group less-margin'>
        {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
        <label>
          <Text styledTextProps={{ fontSize: theme.textFontSizes[0] }}>
            Card Information
          </Text>
          <CardElement
            className='form-control'
            style={{ base: { fontSize: '18px' } }}
          />
        </label>
        {stripeId && (
          <NoHoverButton
            className='update-button'
            cta='Use Saved Card'
            customColor={theme.colors.primary || '#3fbfe1'}
            noBorder
            onClick={handleUpdateButton}
            primary
            rounded
          />
        )}
      </FormGroup>
    </DefaultPaymentWrapper>
  );

  const savedCardOrFields = () => {
    if (isUsingSavedCard) {
      const defaultCard = getDefaultCard();

      if (!defaultCard) {
        return defaultCardFields();
      }

      return (
        <FormGroup className='less-margin'>
          {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
          <label>
            <Text styledTextProps={{ fontSize: theme.textFontSizes[0] }}>
              Saved Payment
            </Text>
            <PaymentCard
              brand={defaultCard.brand}
              handleUpdateButton={handleUpdateButton}
              isLoading={isLoadingPaymentDetails}
              last4={defaultCard.last4}
            />
            <NoHoverButton
              className='update-button'
              cta='Use A New Card'
              customColor={theme.colors.primary || '#3fbfe1'}
              noBorder
              onClick={handleUpdateButton}
              primary
              rounded
            />
          </label>
        </FormGroup>
      );
    }
    return defaultCardFields();
  };

  const savedCardView = () => {
    if (isLoadingPaymentDetails) {
      return (
        <FormGroup className='form-group'>
          {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
          <label>
            <Shimmer>
              <Text styledTextProps={{ fontSize: theme.textFontSizes[0] }}>
                Saved Payment
              </Text>
            </Shimmer>
            <PaymentCard isLoading={isLoadingPaymentDetails} />
            <Shimmer>
              <Text
                styledTextProps={{ fontSize: theme.textFontSizes[0] }}
                styledWrapperProps={{ marginTop: '15px' }}
              >
                Saved Payment
              </Text>
            </Shimmer>
          </label>
        </FormGroup>
      );
    }
    return savedCardOrFields();
  };

  const renderErrorMessages = () => {
    if (!isAlreadySubscribed && !isAdmin && !isNotWithThisAccount) {
      return null;
    }

    return (
      <>
        {isAlreadySubscribed && (
          <FieldError className='text-center' style={{ marginTop: 30 }}>
            This account is already associated with a subscription. Please
            complete the current subscription or sign in to a different
            account to purchase this program.
          </FieldError>
        )}
        {isAdmin && !isNotWithThisAccount && (
          <FieldError className='text-center' style={{ marginTop: 30 }}>
            You are currently signed in to a coach account. To purchase this
            program, please sign in or create an athlete account.
          </FieldError>
        )}
        {isNotWithThisAccount && !isAdmin && (
          <FieldError className='text-center' style={{ marginTop: 30 }}>
            This account is currently associated with
            {' '}
            {organizationName}
            . If
            you would like to buy this program, you will need to deactivate
            this account or create a new account with a different email
            address.
          </FieldError>
        )}
        {isNotWithThisAccount && isAdmin && (
          <FieldError className='text-center' style={{ marginTop: 30 }}>
            You are currently signed in to a coach account and this account is
            associated with
            {' '}
            {organizationName}
            . If you would like to buy this
            program, you will need to deactivate this account or create a new
            athlete account with a different email address.
          </FieldError>
        )}
      </>
    );
  };

  return (
    <Form id='checkout' name='checkout'>
      <InfoCard
        InfoDetails={(
          <InfoDetailsWrapper>
            <Text className='coach-name' styledTextProps={{ fontSize: 12 }}>
              {resellerName}
            </Text>
            <Text styledTextProps={{ fontWeight: 600 }}>{programName}</Text>
          </InfoDetailsWrapper>
        )}
        InfoSideDetails={(
          <PriceContainer>
            <LargePrice>
              {placeSymbolAfterCurrency
                ? `${price}${currencySymbol}`
                : `${currencySymbol}${price}`}
            </LargePrice>
            <SmallPrice>
              {billingFrequency === ONE_TIME_BILLING_FREQUENCY
                ? billingFrequencyDescription
                : `/${billingFrequencyDescription}`}
            </SmallPrice>
          </PriceContainer>
        )}
      />
      {paymentError && (
        <FieldError className='text-center'>
          {paymentError.message
            || 'Something went wrong, please try again. If the problem persists, try again later.'}
        </FieldError>
      )}
      {isAlreadySubscribed || isAdmin || isNotWithThisAccount ? (
        renderErrorMessages()
      ) : (
        <>
          {stripeId ? savedCardView() : defaultCardFields()}
          <CouponCodeContainer>
            <FormGroup className='form-group'>
              <Text
                styledTextProps={{
                  fontSize: theme.textFontSizes[0],
                  fontWeight: 700,
                }}
              >
                Coupon Code
              </Text>
              <CouponInputAndButtonWrapper>
                <CouponInputContainer>
                  <Field
                    className='form-control'
                    id='coupon-field'
                    name='couponId'
                    placeholder='Coupon Code'
                    type='text'
                    onChange={handleCouponChange}
                  />
                </CouponInputContainer>
                <CouponButtonContainer>
                  <NoHoverButton
                    id='apply'
                    name='apply'
                    cta='Apply'
                    className='apply-button'
                    customColor={theme.colors.success || '#10CD8C'}
                    disabled={isLoadingCoupon || !values.couponId}
                    fullWidth
                    isLoading={isLoadingCoupon}
                    large
                    noBorder
                    onClick={handleApplyCoupon}
                    primary
                    rounded
                    type='button'
                  />
                </CouponButtonContainer>
              </CouponInputAndButtonWrapper>
              {couponError && (
                <FieldError className='error-text coupon-error'>
                  {couponError}
                </FieldError>
              )}
            </FormGroup>
          </CouponCodeContainer>
          {coupon && !couponError && (
            <FormGroup className='form-group prices'>
              <Text styledTextProps={priceTextProps}>
                Price
              </Text>
              <Text styledTextProps={priceTextProps}>
                {formatCurrency(price)}
              </Text>
            </FormGroup>
          )}
          {coupon && !couponError && (
            <FormGroup className='form-group prices'>
              <Text
                styledTextProps={{
                  ...priceTextProps,
                  color: theme.colors.green,
                }}
              >
                {coupon.name.toUpperCase()}
              </Text>
              <Text styledTextProps={priceTextProps}>
                {getPriceAndCurrency()}
              </Text>
            </FormGroup>
          )}
          <FormGroup className='form-group prices'>
            <Text styledTextProps={priceTextProps}>
              Total
            </Text>
            <Text styledTextProps={priceTextProps}>
              {formatCurrency(total)}
            </Text>
          </FormGroup>
          <FormGroup className='d-flex'>
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
            <label className='d-flex' htmlFor='agreementData'>
              <input
                className='checkbox-input'
                id='agreementData'
                name='agreementData'
                type='checkbox'
                checked={values.agreementData}
                onChange={handleAgreementChange}
              />
              <Text styledTextProps={{ fontSize: theme.textFontSizes[0] }}>
                I understand that all tools and information presented on this
                site are intended for informational and educational purposes.
                Any diet or exercise programs and advice are NOT intended as
                medical diagnosis or treatment. You should consult your doctor
                before beginning any diet or exercise program.
              </Text>
            </label>
          </FormGroup>
          {errors.agreementData && touched.agreementData && (
            <FieldError className='text-center'>
              Must Accept Terms Before Subscribing.
            </FieldError>
          )}
          {numBillingCycles !== null && numBillingCycles > 0 && (
            <FormGroup className='d-flex'>
              <Text styledTextProps={{ fontSize: theme.textFontSizes[0] }}>
                {getProgramEndDate()}
              </Text>
            </FormGroup>
          )}
          <FormGroup className='text-center'>
            {isLoadingPayment ? (
              <Spinner darkTheme large saving />
            ) : (
              <ButtonPadding>
                <NoHoverButton
                  id='submit'
                  name='submit'
                  cta='Confirm Payment'
                  className='apply-button'
                  customColor={theme.colors.success || '#10CD8C'}
                  disabled={isCheckoutDisabled}
                  fullWidth
                  large
                  noBorder
                  onClick={handleCheckoutSubmit}
                  primary
                  rounded
                  type='button'
                />
              </ButtonPadding>
            )}
          </FormGroup>
          {trialPeriod !== null && trialPeriod > 0 && (
            <FormGroup className='d-flex'>
              <Text styledTextProps={{ fontSize: theme.textFontSizes[0] }}>
                {`* Your card will not be charged at time of purchase. The amount of the program will be charged on this card after ${trialPeriod} days.`}
              </Text>
            </FormGroup>
          )}
        </>
      )}
    </Form>
  );
};

FormCheckout.propTypes = {
  elements: PropTypes.instanceOf(Object).isRequired,
  formProps: PropTypes.instanceOf(Object).isRequired,
  stripe: PropTypes.instanceOf(Object).isRequired,
};

export default injectStripe(FormCheckout);
